import { useCallback, useMemo } from 'react';
import { clsx } from 'clsx';
import { TenantContext, useActiveRealmAPIContext } from '@interxfi/api-client/realm-context';
import { HorizontalStack } from '@interxfi/design-system/components/alignment/horizontal-stack';
import { VerticalStack } from '@interxfi/design-system/components/alignment/vertical-stack';
import { Button } from '@interxfi/design-system/components/button/button';
import { Tooltip } from '@interxfi/design-system/components/tooltip/tooltip';
import ArrowDiagIcon from '@interxfi/design-system/icons/react/icon-arrow-diag';
import AvatarIcon from '@interxfi/design-system/icons/react/icon-avatar';
import BankIcon from '@interxfi/design-system/icons/react/icon-bank';
import BillIcon from '@interxfi/design-system/icons/react/icon-bill';
import ClockIcon from '@interxfi/design-system/icons/react/icon-clock';
import ClusterIcon from '@interxfi/design-system/icons/react/icon-cluster';
import CompanyIcon from '@interxfi/design-system/icons/react/icon-company';
import DashboardIcon from '@interxfi/design-system/icons/react/icon-dashboard';
import DeadletterIcon from '@interxfi/design-system/icons/react/icon-deadletter';
import DevelopersIcon from '@interxfi/design-system/icons/react/icon-developers';
import DisclosureLeftSmallIcon from '@interxfi/design-system/icons/react/icon-disclosure-left-small';
import EnvironmentIcon from '@interxfi/design-system/icons/react/icon-environment';
import FolderIcon from '@interxfi/design-system/icons/react/icon-folder';
import GuidesIcon from '@interxfi/design-system/icons/react/icon-guides';
import OnboardingIcon from '@interxfi/design-system/icons/react/icon-onboarding';
import OperationsIcon from '@interxfi/design-system/icons/react/icon-operations';
import OrdersIcon from '@interxfi/design-system/icons/react/icon-orders';
import OrgIcon from '@interxfi/design-system/icons/react/icon-org';
import PaymentIcon from '@interxfi/design-system/icons/react/icon-payment';
import ProductIcon from '@interxfi/design-system/icons/react/icon-product';
import OrgRealm from '@interxfi/design-system/icons/react/icon-realm';
import RocketIcon from '@interxfi/design-system/icons/react/icon-rocket';
import SecuritiesIcon from '@interxfi/design-system/icons/react/icon-securities';
import ShieldIcon from '@interxfi/design-system/icons/react/icon-shield';
import StackIcon from '@interxfi/design-system/icons/react/icon-stack';
import SweepsIcon from '@interxfi/design-system/icons/react/icon-sweeps';
import TeamIcon from '@interxfi/design-system/icons/react/icon-team';
import RolesIcon from '@interxfi/design-system/icons/react/icon-toggle';
import UnlockIcon from '@interxfi/design-system/icons/react/icon-unlock';
import type { IxmUserauthV1TenantState } from '@interxfi/types';
import { ASSET_BASE_PATH } from '../../../features/asset/asset-routing';
import { BILLING_CHARGE_BASE_PATH } from '../../../features/billing/charge/billing-charge-routing';
import { InboxNavIcon } from '../../../features/case-management/inbox/nav-icon/inbox-nav-icon';
import { InboxStatusBadge } from '../../../features/case-management/inbox/status-badge/inbox-status-badge';
import { O5_CLUSTER_BASE_PATH } from '../../../features/o5/cluster/o5-cluster-routing';
import { O5_DEPLOYMENT_BASE_PATH } from '../../../features/o5/deployment/o5-deployment-routing';
import { O5_ENVIRONMENT_BASE_PATH } from '../../../features/o5/environment/o5-environment-routing';
import { O5_STACK_BASE_PATH } from '../../../features/o5/stack/o5-stack-routing';
import { ORDER_BASE_PATH } from '../../../features/order/order-routing';
import { getOrganizationDetailPath, USERAUTH_ORGANIZATION_BASE_PATH } from '../../../features/userauth/organization/userauth-organization-routing';
import { PROGRAM_BASE_PATH } from '../../../features/userauth/program/program-routing';
import { USERAUTH_REALM_BASE_PATH } from '../../../features/userauth/realm/userauth-realm-routing';
import { USERAUTH_REALM_ACCESS_BASE_PATH } from '../../../features/userauth/realm-access/userauth-realm-access-routing';
import { USERAUTH_ROLE_BASE_PATH } from '../../../features/userauth/role/userauth-role-routing';
import { USERAUTH_TENANT_BASE_PATH } from '../../../features/userauth/tenant/userauth-tenant-routing';
import { useRealmIndicatorContext } from '../../realm/realm-indicator-context';
import { RealmPill } from '../../realm/realm-pill';
import { useLayoutContext } from '../context';
import { ContextMenu } from './context-menu/context-menu';
import { SidebarNavGroup } from './nav-group/sidebar-nav-group';
import type { SidebarNavItemGroup, SidebarNavItemType } from './nav-item/sidebar-nav-item';
import styles from './sidebar.module.scss';

const navItems = {
  dashboard: {
    label: 'Dashboard',
    prefix: <DashboardIcon aria-hidden />,
    to: '/dashboard',
  },
  inbox: {
    label: 'Inbox',
    prefix: <InboxNavIcon />,
    affix: <InboxStatusBadge />,
    to: '/inbox',
  },
  registration: {
    label: 'Registration',
    prefix: <AvatarIcon aria-hidden />,
    to: '/registration',
  },
  orders: {
    label: 'Orders',
    prefix: <OrdersIcon aria-hidden />,
    to: ORDER_BASE_PATH,
  },
  funding: {
    label: 'Funding',
    prefix: <BankIcon aria-hidden />,
    to: '/funding',
  },
  assets: {
    label: 'Assets',
    prefix: <SecuritiesIcon aria-hidden />,
    to: ASSET_BASE_PATH,
  },
  sweeps: {
    label: 'Sweeps',
    prefix: <SweepsIcon aria-hidden />,
    to: '/sweep',
  },
  billing: {
    label: 'Billing',
    prefix: <BillIcon aria-hidden />,
    to: BILLING_CHARGE_BASE_PATH,
  },
  backofficeOperations: {
    label: 'Operations',
    prefix: <OperationsIcon aria-hidden />,
    to: '/clearing/operations',
  },
  backofficeFinance: {
    label: 'Finance',
    prefix: <PaymentIcon aria-hidden />,
    to: '/clearing/finance',
  },
  backofficeCompliance: {
    label: 'Compliance',
    prefix: <ShieldIcon aria-hidden />,
    to: '/clearing/compliance',
  },
  backofficeVendors: {
    label: 'Vendors',
    prefix: <OrgIcon aria-hidden />,
    to: '/clearing/vendor',
  },
  developerDashboard: {
    label: 'Dashboard',
    prefix: <DevelopersIcon aria-hidden />,
    to: '/developer',
    tooltipLabel: 'Developers',
  },
  apiDocs: {
    label: 'API Docs',
    prefix: <GuidesIcon aria-hidden />,
    affix: <ArrowDiagIcon aria-hidden />,
    to: '/docs',
  },
  techOpsDeployments: {
    label: 'Deployments',
    prefix: <RocketIcon aria-hidden />,
    to: O5_DEPLOYMENT_BASE_PATH,
  },
  techOpsEnvironments: {
    label: 'Environments',
    prefix: <EnvironmentIcon aria-hidden />,
    to: O5_ENVIRONMENT_BASE_PATH,
  },
  techOpsStacks: {
    label: 'Stacks',
    prefix: <StackIcon aria-hidden />,
    to: O5_STACK_BASE_PATH,
  },
  techOpsClusters: {
    label: 'Clusters',
    prefix: <ClusterIcon aria-hidden />,
    to: O5_CLUSTER_BASE_PATH,
  },
  techOpsDeadLetters: {
    label: 'Dead Letters',
    prefix: <DeadletterIcon aria-hidden />,
    to: '/tech-ops/dead-message',
  },
  techOpsBuildRepos: {
    label: 'Repos',
    prefix: <FolderIcon aria-hidden />,
    to: '/tech-ops/build/repo',
  },
  techOpsTrigger: {
    label: 'Triggers',
    prefix: <ClockIcon aria-hidden />,
    to: '/tech-ops/trigger',
  },
  techOpsE2eTestRun: {
    label: 'End-to-End',
    prefix: <OnboardingIcon aria-hidden />,
    to: '/tech-ops/test-run',
  },
  platformUsers: {
    label: 'Users',
    prefix: <AvatarIcon aria-hidden />,
    to: '/user',
  },
  platformOrganizations: {
    label: 'Organizations',
    prefix: <CompanyIcon aria-hidden />,
    to: USERAUTH_ORGANIZATION_BASE_PATH,
  },
  platformPrograms: {
    label: 'Programs',
    prefix: <ProductIcon aria-hidden />,
    to: PROGRAM_BASE_PATH,
  },
  platformRoles: {
    label: 'Roles',
    prefix: <RolesIcon aria-hidden />,
    to: USERAUTH_ROLE_BASE_PATH,
  },
  platformRealms: {
    label: 'Realms',
    prefix: <OrgRealm aria-hidden />,
    to: USERAUTH_REALM_BASE_PATH,
  },
  platformRealmAccess: {
    label: 'Realm Access',
    prefix: <UnlockIcon aria-hidden />,
    to: USERAUTH_REALM_ACCESS_BASE_PATH,
  },
  platformTenants: {
    label: 'Tenants',
    prefix: <TeamIcon aria-hidden />,
    to: USERAUTH_TENANT_BASE_PATH,
  },
  organizationSettings: {
    label: 'Organization',
    prefix: <CompanyIcon aria-hidden />,
    to: USERAUTH_ORGANIZATION_BASE_PATH,
  },
} as const satisfies Record<string, Omit<SidebarNavItemType, 'id'>>;

function getNavItem(id: keyof typeof navItems): SidebarNavItemType {
  return { id, ...navItems[id] };
}

function getSidebarItems(activeTenant: IxmUserauthV1TenantState | undefined): SidebarNavItemGroup[] {
  switch (activeTenant?.tenantType) {
    case TenantContext.O5:
      return [
        {
          id: 'main-tech-ops',
          items: [
            getNavItem('techOpsDeployments'),
            getNavItem('techOpsEnvironments'),
            getNavItem('techOpsStacks'),
            getNavItem('techOpsClusters'),
            getNavItem('techOpsDeadLetters'),
            getNavItem('techOpsBuildRepos'),
            getNavItem('techOpsTrigger'),
            getNavItem('techOpsE2eTestRun'),
          ],
        },
      ];
    case TenantContext.Clearing:
      return [
        {
          id: 'main-default',
          items: [
            getNavItem('inbox'),
            getNavItem('registration'),
            getNavItem('orders'),
            getNavItem('funding'),
            getNavItem('assets'),
            getNavItem('sweeps'),
            getNavItem('billing'),
          ],
        },
        {
          id: 'developers',
          label: 'Developers',
          items: [getNavItem('developerDashboard'), getNavItem('apiDocs')],
        },
        {
          id: 'backOffice',
          label: 'Back Office',
          items: [
            getNavItem('backofficeOperations'),
            getNavItem('backofficeFinance'),
            getNavItem('backofficeCompliance'),
            getNavItem('backofficeVendors'),
          ],
        },
      ];
    case TenantContext.Organization: {
      const organizationSettingsItem = getNavItem('organizationSettings');
      return [
        {
          id: 'main-organization',
          items: [
            getNavItem('platformUsers'),
            getNavItem('platformRoles'),
            getNavItem('platformPrograms'),
            { ...organizationSettingsItem, to: getOrganizationDetailPath(activeTenant?.tenantId) || organizationSettingsItem.to },
          ],
        },
      ];
    }
    case TenantContext.Platform:
      return [
        {
          id: 'main-platform',
          items: [
            getNavItem('platformUsers'),
            getNavItem('platformRoles'),
            getNavItem('platformOrganizations'),
            getNavItem('platformPrograms'),
            getNavItem('platformRealms'),
            getNavItem('platformRealmAccess'),
            getNavItem('platformTenants'),
          ],
        },
      ];
    default:
      return [
        {
          id: 'main-default',
          items: [
            getNavItem('dashboard'),
            getNavItem('registration'),
            getNavItem('orders'),
            getNavItem('funding'),
            getNavItem('assets'),
            getNavItem('sweeps'),
            getNavItem('billing'),
          ],
        },
        {
          id: 'developers',
          label: 'Developers',
          items: [getNavItem('developerDashboard'), getNavItem('apiDocs')],
        },
      ];
  }
}

export function Sidebar() {
  const { activeTenant, activeRealm } = useActiveRealmAPIContext();
  const { showBanner: showRealmIndicatorBanner } = useRealmIndicatorContext();
  const { collapsedSidebarStatus, onSidebarCollapseChange, isMobile, showCollapsedSidebar } = useLayoutContext();
  const sidebarGroups = useMemo(() => getSidebarItems(activeTenant), [activeTenant]);
  const handleCollapseSidebar = useCallback(() => {
    onSidebarCollapseChange(!showCollapsedSidebar);
  }, [onSidebarCollapseChange, showCollapsedSidebar]);
  const isCollapsing = collapsedSidebarStatus === 'collapsing';

  const realmPill =
    activeRealm?.data?.spec?.name && !['meta', 'techops'].includes(activeRealm.data.spec.name?.toLowerCase()) ? (
      <HorizontalStack
        align={showCollapsedSidebar ? (isCollapsing ? 'start' : 'center') : undefined}
        as={showCollapsedSidebar ? 'button' : undefined}
        blockAlign={showCollapsedSidebar ? 'center' : undefined}
        className={clsx(styles.realmPillContainer, styles[`state-${collapsedSidebarStatus}`])}
      >
        <RealmPill className={clsx(styles.pill, !showRealmIndicatorBanner && styles.isVisible)} realmName={activeRealm.data.spec.name}>
          {showCollapsedSidebar ? activeRealm.data.spec.name.charAt(0) : activeRealm.data.spec.name}
        </RealmPill>
      </HorizontalStack>
    ) : null;

  return (
    <aside className={clsx(styles.sidebar, showCollapsedSidebar && styles.isCollapsed, isMobile && styles.isMobile)}>
      <VerticalStack align="space-between" className={styles.container} gap={4}>
        <VerticalStack>
          <ContextMenu />

          <VerticalStack>
            {showCollapsedSidebar ? (
              <Tooltip activator={realmPill} side="right" sideOffset={-8} withArrow={false}>
                {activeRealm?.data?.spec?.name}
              </Tooltip>
            ) : (
              realmPill
            )}

            <VerticalStack as="ul" className={styles.navGroups} gap={1}>
              {sidebarGroups.map((group) => (
                <li key={group.id}>
                  <SidebarNavGroup {...group} />
                </li>
              ))}
            </VerticalStack>
          </VerticalStack>
        </VerticalStack>

        {!isMobile ? (
          <HorizontalStack align="end" blockAlign="center" className={styles.expander}>
            <Button
              affixIcon={<DisclosureLeftSmallIcon aria-hidden />}
              aria-label={showCollapsedSidebar ? 'Expand navigation' : 'Collapse navigation'}
              className={styles.expanderButton}
              onClick={handleCollapseSidebar}
              size="small"
              variant="light"
            />
          </HorizontalStack>
        ) : null}
      </VerticalStack>
    </aside>
  );
}
